import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import CustomInput from '../../../components/textField/scmInput/CustomInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { formatToUtcIso } from '../../../utils/timeFormat';
import LoadingOverlay from '../../../components/loadingoverlay/LoadingOverlay';
import './CreateDeployment.css';
import { getPackageInfo } from '../../../services/scm/Package';
import { getTrackDeploymentsList } from '../../../services/scm/TrackDeploymentAPI';

export default function CreateDeployment({ title = 'Create Deployment' }) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const navState = location.state || {};

    const statePkgName = navState.packageName || null;
    const stateVersionName = navState.versionName || null;

    // values coming from "Edit" navigation (if any)
    const stateDeploymentName = navState.deploymentName || '';
    const stateDescription = navState.description || '';
    const stateJobType = navState.jobType || '';
    const stateRetries = navState.retries;
    const stateStartMode = navState.startMode || 'IMMEDIATELY';
    const stateEndMode = navState.endMode || 'NO_END';
    const stateStartTime = navState.startTime || '';
    const stateEndTime = navState.endTime || '';

    const [pkg, setPkg] = useState(null);
    const [pkgLoading, setPkgLoading] = useState(false);

    // ---- FORM STATE (PRE-POPULATED FROM navState WHEN PRESENT) ----
    const [deploymentName, setDeploymentName] = useState(stateDeploymentName);
    const [description, setDescription] = useState(stateDescription);
    const [jobType, setJobType] = useState(stateJobType); // SNAPSHOT | CONTINUOUS

    const [existingNames, setExistingNames] = useState([]);
    const [checkingName, setCheckingName] = useState(false);
    const [isNameUnique, setIsNameUnique] = useState(true);


    // ---- REDIRECT IF NO PACKAGE / VERSION IN STATE ----
    useEffect(() => {
        if (!statePkgName || !stateVersionName) {
            toast.error(t('Package and Version are required'));
            navigate('/software-management/find-package-deployment', { replace: true });
            return;
        }
        fetchPackage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statePkgName, stateVersionName, navigate, t]);

    useEffect(() => {
        async function loadNames() {
            try {
                const res = await getTrackDeploymentsList();

                const names =
                    (res?.jobs || []).map(j => j.jobId?.toLowerCase());

                setExistingNames(names);
            } catch (err) {
                console.error("Failed to load deployment names", err);
            }
        }

        loadNames();
    }, []);

    useEffect(() => {
        const value = deploymentName.trim();

        // If empty → reset everything
        if (!value) {
            setIsNameUnique(true);
            setCheckingName(false);
            return;
        }

        setCheckingName(true);

        const timeout = setTimeout(() => {
            const normalized = value.toLowerCase();
            const original = stateDeploymentName?.toLowerCase().trim();

            const exists =
                existingNames.includes(normalized) &&
                normalized !== original;

            setIsNameUnique(!exists);
            setCheckingName(false);

        }, 300);

        return () => clearTimeout(timeout);

    }, [deploymentName, existingNames, stateDeploymentName]);



    const fetchPackage = async () => {
        try {
            setPkgLoading(true);

            // getPackageInfo returns flat array
            const flatList = await getPackageInfo({ packageName: statePkgName });

            const found = flatList.find(
                (p) =>
                    p.packageName === statePkgName &&
                    p.version === stateVersionName // version field from helper (e.g. "v1.0.0")
            );

            if (!found) {
                toast.error(t('Package/version not found'));
                navigate(-1);
                return;
            }

            setPkg(found);
        } catch (err) {
            console.error('Failed to load package info', err);
            toast.error(t('Failed to load package info'));
            navigate(-1);
        } finally {
            setPkgLoading(false);
        }
    };

    const pageTitle = t(title);

    const [retries, setRetries] = useState(
        stateRetries === 0 || stateRetries
            ? String(stateRetries)
            : ''
    );

    // Deploy start time
    const [startMode, setStartMode] = useState(stateStartMode); // 'IMMEDIATELY' | 'SCHEDULE'
    const [startTime, setStartTime] = useState(
        stateStartMode === 'SCHEDULE' && stateStartTime
            ? dayjs(stateStartTime)
            : dayjs()
    );

    // Deploy end time
    const [endMode, setEndMode] = useState(stateEndMode); // 'NO_END' | 'HAS_END'
    const [endTime, setEndTime] = useState(
        stateEndMode === 'HAS_END' && stateEndTime
            ? dayjs(stateEndTime)
            : dayjs().add(1, 'hour')
    );

    const [saveLoading, setSaveLoading] = useState(false);

    // ---- HELPERS ----
    const Required = () => <span className="text-danger ms-1">*</span>;
    const isNonEmpty = (v) => String(v ?? '').trim().length > 0;

    const retriesNumber = useMemo(() => {
        if (retries === '') return NaN;
        const n = Number(retries);
        return Number.isNaN(n) ? NaN : n;
    }, [retries]);

    const isRetriesValid =
        !Number.isNaN(retriesNumber) &&
        retriesNumber >= 0 &&
        retriesNumber <= 10;

    const isStartTimeValid =
        startMode === 'IMMEDIATELY' || (startMode === 'SCHEDULE' && !!startTime);

    const isEndTimeValid =
        endMode === 'NO_END' ||
        (endMode === 'HAS_END' && !!endTime && (!startTime || endTime.isAfter(startTime)));

    const isFormValid =
        isNonEmpty(statePkgName) &&
        isNonEmpty(stateVersionName) &&
        isNonEmpty(deploymentName) &&
        isNonEmpty(jobType) &&
        isRetriesValid &&
        isStartTimeValid &&
        isEndTimeValid &&
        isNameUnique &&
        !checkingName;


    // ---- RESET FORM ----
    const resetForm = () => {
        setDeploymentName('');
        setDescription('');
        setJobType('');
        setRetries('');
        setStartMode('IMMEDIATELY');
        setStartTime(dayjs());
        setEndMode('NO_END');
        setEndTime(dayjs().add(1, 'hour'));
    };

    // ---- SAVE ----
    const handleSave = () => {
        if (!isFormValid || saveLoading) return;

        // Navigate to Select Targets page with the payload for step 2
        navigate('/software-management/select-targets', {
            state: {
                packageName: statePkgName,
                versionName: stateVersionName,
                deploymentName,
                description,
                jobType,
                retries: retriesNumber,
                startMode,
                endMode,
                startTime:
                    startMode === 'SCHEDULE'
                        ? formatToUtcIso(startTime)
                        : '',
                endTime:
                    endMode === 'HAS_END'
                        ? formatToUtcIso(endTime)
                        : '',
            },
        });
    };

    return (
        <div className="container my-3">
            <div className="card-body col-12 col-xxl-10 responsive-col">
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <h5 className="card-title mb-0">{pageTitle}</h5>
                </div>

                {/* Package & Version with icon right next to labels */}
                <div className="row g-3 mb-1">
                    {/* Package Name */}
                    <div className="col-12 col-md-6">
                        <div className="d-flex align-items-center gap-1">
                            <label className="form-label mb-0">
                                {t('Package Name')} <Required />
                            </label>

                            <button
                                type="button"
                                className="btn btn-sm btn-transperant"
                                title={t('Change Package')}
                                onClick={() =>
                                    navigate('/software-management/find-package-deployment')
                                }
                            >
                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                            </button>
                        </div>

                        <input
                            type="text"
                            readOnly
                            className="form-control-plaintext readonly-input ps-2"
                            value={statePkgName || ''}
                        />
                    </div>

                    {/* Version Name */}
                    <div className="col-12 col-md-6">
                        <div className="d-flex align-items-center">
                            <label className="form-label mb-0">
                                {t('Version Name')} <Required />
                            </label>

                            <button
                                type="button"
                                className="btn btn-sm btn-transperant"
                                title={t('Change Version')}
                                onClick={() =>
                                    navigate('/software-management/find-package-deployment')
                                }
                            >
                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                            </button>
                        </div>

                        <input
                            type="text"
                            readOnly
                            className="form-control-plaintext readonly-input ps-2"
                            value={stateVersionName || ''}
                        />
                    </div>
                </div>

                {/* Deployment basic info */}
                <div className="row g-2">
                    <div className="col-12 col-md-6">
                        <CustomInput
                            label={
                                <>
                                    {t('Deployment name')}
                                    <Required />
                                </>
                            }
                            value={deploymentName}
                            onChange={(e) => setDeploymentName(e.target.value)}
                            placeholder={t('Enter deployment name')}
                            allowedChars={/^[A-Za-z0-9_-]*$/}
                        />
                        {/* Required */}
                        {!isNonEmpty(deploymentName) && (
                            <small className="text-danger d-block">
                                {t('Deployment name is required')}
                            </small>
                        )}

                        {/* Duplicate */}
                        {isNonEmpty(deploymentName) && !isNameUnique && !checkingName && (
                            <small className="text-danger d-block">
                                {t('Deployment name already exists')}
                            </small>
                        )}

                        {/* Loading */}
                        {isNonEmpty(deploymentName) && checkingName && (
                            <small className="text-muted d-block">
                                {t('Checking availability...')}
                            </small>
                        )}


                    </div>

                    <div className="col-12 col-md-6">
                        <CustomInput
                            label={t('Description')}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={t('Enter Description')}
                        />
                    </div>

                    <div className="col-12 col-md-4">
                        <label className="form-label">
                            {t('Job Type')} <Required />
                        </label>
                        <select
                            value={jobType}
                            onChange={(e) => setJobType(e.target.value)}
                            className="form-select"
                        >
                            <option value="">{t('Select Job Type')}</option>
                            <option value="SNAPSHOT">SNAPSHOT</option>
                            <option
                                value="CONTINUOUS"
                                disabled={pkg?.dependencies?.length === 0}
                            >
                                CONTINUOUS
                            </option>
                        </select>
                        {!isNonEmpty(jobType) && (
                            <small className="text-danger">
                                {t('Job Type is required')}
                            </small>
                        )}
                    </div>

                    <div className="col-12 col-md-4">
                        <CustomInput
                            label={
                                <>
                                    {t('Retries (0 - 10)')}
                                    <Required />
                                </>
                            }
                            value={retries}
                            onChange={(e) => setRetries(e.target.value)}
                            placeholder="0"
                            allowedChars={/^\d*$/}
                        />
                        {(!isRetriesValid || retries === '') && (
                            <small className="text-danger">
                                {t('Retries must be a number between 0 and 10')}
                            </small>
                        )}
                    </div>
                </div>

                {/* Deploy Start / End Time */}
                <div className="card mt-3">
                    <div className="card-body">
                        <h6 className="card-subtitle mb-1">
                            {t('Deployment Schedule')}
                        </h6>

                        {/* Start time */}
                        <div className="row g-2 align-items-center mb-1">
                            <div className="col-12 col-md-8">
                                <label className="form-label d-block">
                                    {t('Deployment Start Time')} <Required />
                                </label>

                                <div className="d-flex align-items-center gap-2 flex-nowrap">
                                    <div
                                        className="btn-group flex-shrink-0"
                                        role="group"
                                        aria-label="deployment start mode"
                                    >
                                        <button
                                            type="button"
                                            className={`btn text-nowrap ${startMode === 'IMMEDIATELY'
                                                ? 'btn-primary'
                                                : 'btn-outline-secondary'
                                                }`}
                                            onClick={() => setStartMode('IMMEDIATELY')}
                                        >
                                            {t('Immediately')}
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn text-nowrap ${startMode === 'SCHEDULE'
                                                ? 'btn-primary'
                                                : 'btn-outline-secondary'
                                                }`}
                                            onClick={() => setStartMode('SCHEDULE')}
                                        >
                                            {t('Schedule')}
                                        </button>
                                    </div>

                                    {startMode === 'SCHEDULE' && (
                                        <div className="flex-grow-1">
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DateTimePicker
                                                    value={startTime}
                                                    onChange={(val) => setStartTime(val)}
                                                    slotProps={{
                                                        textField: {
                                                            fullWidth: true,
                                                            variant: 'outlined',
                                                            size: 'small',
                                                        },
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </div>
                                    )}
                                </div>

                                {!isStartTimeValid && (
                                    <small className="text-danger">
                                        {t('Start time is required when Scheduled')}
                                    </small>
                                )}
                            </div>
                        </div>

                        {/* End time */}
                        <div className="row g-2 align-items-center">
                            <div className="col-12 col-md-8">
                                <label className="form-label d-block">
                                    {t('Deployment End Time')}
                                </label>

                                <div className="d-flex align-items-center gap-2 flex-nowrap">
                                    <div
                                        className="btn-group flex-shrink-0"
                                        role="group"
                                        aria-label="deployment end mode"
                                    >
                                        <button
                                            type="button"
                                            className={`btn text-nowrap ${endMode === 'NO_END'
                                                ? 'btn-primary'
                                                : 'btn-outline-secondary'
                                                }`}
                                            onClick={() => setEndMode('NO_END')}
                                        >
                                            {t('No end')}
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn text-nowrap ${endMode === 'HAS_END'
                                                ? 'btn-primary'
                                                : 'btn-outline-secondary'
                                                }`}
                                            onClick={() => setEndMode('HAS_END')}
                                        >
                                            {t('Set end time')}
                                        </button>
                                    </div>

                                    {endMode === 'HAS_END' && (
                                        <div className="flex-grow-1">
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DateTimePicker
                                                    value={endTime}
                                                    onChange={(val) => setEndTime(val)}
                                                    slotProps={{
                                                        textField: {
                                                            fullWidth: true,
                                                            variant: 'outlined',
                                                            size: 'small',
                                                        },
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </div>
                                    )}
                                </div>

                                {!isEndTimeValid && (
                                    <small className="text-danger">
                                        {t('End time must be after start time')}
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer buttons */}
            <div className="mt-4 d-flex justify-content-end gap-2 col-12 col-xxl-10 responsive-col">
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate("/software-management/find-package-deployment")}
                >
                    {t('Cancel')}
                </button>

                <button
                    className="btn btn-primary"
                    disabled={!isFormValid || saveLoading}
                    onClick={handleSave}
                >
                    {t('Next')}
                </button>
            </div>

            {(saveLoading || pkgLoading) && <LoadingOverlay />}
        </div>
    );
}
